import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Typography, ButtonGroup, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, TextField, IconButton, Collapse } from '@mui/material';
import { FilterList as FilterListIcon, Clear as ClearIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { bookingIncludesTherapist, getBookingTherapistNames } from '../utils/bookings';
import { therapistMatchesSearchTokens } from '../utils/rosterUtils';

// Legend with bookings and block types
const legend = [
  { label: 'Booking', bg: 'rgba(46, 184, 92, 0.18)', bd: 'rgba(46, 184, 92, 0.35)' },
  { label: 'Booking during break', bg: 'rgba(254, 202, 202, 0.45)', bd: 'rgba(220, 38, 38, 0.65)' },
  { label: 'Blocked', bg: 'rgba(198, 40, 40, 0.2)', bd: 'rgba(198, 40, 40, 0.35)' },
  { label: 'Break', bg: 'rgba(255, 152, 0, 0.2)', bd: 'rgba(255, 152, 0, 0.35)' },
  { label: 'Meeting', bg: 'rgba(33, 150, 243, 0.2)', bd: 'rgba(33, 150, 243, 0.35)' },
  { label: 'Training', bg: 'rgba(156, 39, 176, 0.2)', bd: 'rgba(156, 39, 176, 0.35)' },
];

const parseTimeToMinutes = (value) => {
  const [hours = 0, minutes = 0] = String(value || '0:0').split(':').map(Number);
  return hours * 60 + minutes;
};

const rangesOverlap = (startA, endA, startB, endB) => startA < endB && startB < endA;

const normalizeRoleKey = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const formatRoleLabel = (value) => {
  const s = String(value || '').trim();
  if (!s) return '';
  return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
};

const getStaffRole = (t) => String(t?.role || t?.designation || t?.title || '').trim();

const getBookingTreatmentInstruction = (booking) =>
  String(booking?.treatmentInstruction || booking?.treatment_instruction || '').trim();

const getBookingGeneralInstruction = (booking) =>
  String(booking?.generalInstruction || booking?.general_instruction || '').trim();

const getBookingGuestName = (booking) => booking?.guestName || booking?.guest_name || '';

const formatBookingBlockTitle = (booking) => {
  const guest = getBookingGuestName(booking);
  const instruction = getBookingTreatmentInstruction(booking);
  const general = getBookingGeneralInstruction(booking);
  const parts = [
    booking?.treatment,
    guest,
    instruction,
    general ? `General: ${general}` : '',
  ].filter(Boolean);
  return parts.join(' | ');
};

const DutyRoster = () => {
  const { user, hasPermission } = useAuth();
  const isAdmin = hasPermission('admin', 'view') || user?.is_superuser;
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [therapists, setTherapists] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('dutyRosterView') || 'normal');
  const [config, setConfig] = useState({ rooms: {}, treatments: [], therapists: [], equipment: [] });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [reassignTherapist, setReassignTherapist] = useState('');
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [blockForm, setBlockForm] = useState({
    therapist_id: null,
    start_time: '',
    end_time: '',
    block_type: 'BLOCKED',
    reason: '',
    duration: 60
  });
  const [showShiftChangeModal, setShowShiftChangeModal] = useState(false);
  const [selectedTherapistForShift, setSelectedTherapistForShift] = useState(null);
  const [newShiftCode, setNewShiftCode] = useState('');
  const [availableShifts, setAvailableShifts] = useState([]);
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    role: '',
    guestName: '',
    cabin: '',
    guestRoom: '',
    blockType: '',
    searchText: ''
  });

  useEffect(() => { localStorage.setItem('dutyRosterView', viewMode); }, [viewMode]);

  const SLOT_MINUTES = viewMode === 'compact' ? 30 : 15;
  const SLOT_HEIGHT = viewMode === 'compact' ? 28 : 36;

  useEffect(() => {
    const load = async () => {
      const [ther, assn, bks, blks, cfg, shifts] = await Promise.all([
        apiGet('/api/therapists').then(r => r.json()),
        apiGet(`/api/roster/assignments?start_date=${date}&end_date=${date}`).then(r => r.json()),
        apiGet(`/api/bookings/${date}`).then(r => r.json()),
        apiGet(`/api/blocks?date=${date}`).then(r => r.json()).catch(() => []),
        apiGet('/api/config').then(r => r.json()),
        apiGet('/api/shifts').then(r => r.json()).catch(() => [])
      ]);
      setTherapists(Array.isArray(ther) ? ther : []);
      setAssignments(assn || {});
      setBookings(Array.isArray(bks) ? bks : []);
      setBlocks(Array.isArray(blks) ? blks : []);
      setConfig(cfg || { rooms: {}, treatments: [], therapists: [], equipment: [] });
      setAvailableShifts(Array.isArray(shifts) ? shifts : []);
    };
    load();
  }, [date]);

  const rosteredNames = useMemo(() => {
    const day = assignments[date] || {};
    const set = new Set();
    Object.values(day).forEach(payload => {
      const arr = (payload && payload.therapists) || [];
      arr.forEach(n => set.add(n));
    });
    return set;
  }, [assignments, date]);

  // Get therapist shift status (AVAILABLE/UNAVAILABLE)
  const therapistShiftStatus = useMemo(() => {
    const day = assignments[date] || {};
    const statusMap = new Map();
    Object.values(day).forEach(payload => {
      const therapists = (payload && payload.therapists) || [];
      const shiftDetails = (payload && payload.shift_details) || {};
      const availabilityType = shiftDetails.availability_type || 'AVAILABLE';
      therapists.forEach(name => {
        statusMap.set(name, {
          availability_type: availabilityType,
          shift_code: shiftDetails.shift_code || '',
          shift_name: shiftDetails.name || '',
          shift_color: shiftDetails.color || '#f0f0f0'
        });
      });
    });
    return statusMap;
  }, [assignments, date]);

  // Calculate therapist availability summary
  const availabilitySummary = useMemo(() => {
    let available = 0;
    let unavailable = 0;
    let offDuty = 0;

    therapists.forEach(t => {
      const isRostered = rosteredNames.has(t.name);
      if (!isRostered) {
        offDuty++;
      } else {
        const shiftStatus = therapistShiftStatus.get(t.name);
        if (shiftStatus?.availability_type === 'UNAVAILABLE') {
          unavailable++;
        } else {
          available++;
        }
      }
    });

    return { available, unavailable, offDuty };
  }, [therapists, rosteredNames, therapistShiftStatus]);

  const therapistShiftBreaks = useMemo(() => {
    const map = new Map();
    const dayAssignments = assignments[date] || {};
    Object.values(dayAssignments).forEach((shiftData) => {
      const shiftTherapists = shiftData?.therapists || [];
      const breaks = shiftData?.shift_details?.breaks || [];
      shiftTherapists.forEach((name) => {
        if (!map.has(name)) map.set(name, []);
        breaks.forEach((br) => {
          const existing = map.get(name);
          const key = `${br.start_time}-${br.end_time}-${br.break_type || 'BREAK'}`;
          if (!existing.some((item) => `${item.start_time}-${item.end_time}-${item.break_type || 'BREAK'}` === key)) {
            existing.push(br);
          }
        });
      });
    });
    return map;
  }, [assignments, date]);

  const getBookingScheduleConflict = (booking, therapistName) => {
    const start = parseTimeToMinutes(booking.startTime || booking.start_time);
    const end = parseTimeToMinutes(booking.endTime || booking.end_time);
    if (end <= start) return null;

    const shiftBreaks = therapistShiftBreaks.get(therapistName) || [];
    for (const breakPeriod of shiftBreaks) {
      const breakStart = parseTimeToMinutes(breakPeriod.start_time);
      const breakEnd = parseTimeToMinutes(breakPeriod.end_time);
      if (rangesOverlap(start, end, breakStart, breakEnd)) {
        const label = breakPeriod.break_type || 'break';
        return `Overlaps shift ${label} (${breakPeriod.start_time}–${breakPeriod.end_time})`;
      }
    }

    const therapist = therapists.find((t) => t.name === therapistName);
    const therapistBlocks = blocks.filter((block) => block.therapist_id === therapist?.id);
    for (const block of therapistBlocks) {
      if (!['BREAK', 'BLOCKED'].includes(block.block_type)) continue;
      const blockStart = parseTimeToMinutes(block.start_time);
      const blockEnd = parseTimeToMinutes(block.end_time);
      if (rangesOverlap(start, end, blockStart, blockEnd)) {
        return `Overlaps ${block.block_type} (${block.start_time}–${block.end_time})`;
      }
    }

    return null;
  };

  const bookingBreakConflicts = useMemo(() => {
    let count = 0;
    const seen = new Set();
    bookings.forEach((booking) => {
      getBookingTherapistNames(booking).forEach((therapistName) => {
        const key = `${booking.bookingId || booking.id || ''}-${therapistName}-${booking.startTime}`;
        if (seen.has(key)) return;
        if (getBookingScheduleConflict(booking, therapistName)) {
          count += 1;
          seen.add(key);
        }
      });
    });
    return count;
  }, [bookings, therapistShiftBreaks, blocks, therapists]);

  // Extract unique filter options
  const filterOptions = useMemo(() => {
    const roles = new Set();
    const guests = new Set();
    const cabins = new Set();
    const guestRooms = new Set();
    const blockTypes = new Set(['BLOCKED', 'BREAK', 'MEETING', 'TRAINING', 'ADMIN', 'OTHER']);
    
    therapists.forEach(t => {
      const role = getStaffRole(t);
      if (role) roles.add(formatRoleLabel(role));
    });
    
    bookings.forEach(b => {
      if (b.guestName || b.guest_name) guests.add(b.guestName || b.guest_name);
      if (b.room) cabins.add(b.room); // Therapy cabin/treatment room
      if (b.guestRoom || b.guest_room) guestRooms.add(b.guestRoom || b.guest_room); // Guest accommodation room
    });
    
    return {
      roles: Array.from(roles).sort(),
      guests: Array.from(guests).sort(),
      cabins: Array.from(cabins).sort(),
      guestRooms: Array.from(guestRooms).sort(),
      blockTypes: Array.from(blockTypes)
    };
  }, [therapists, bookings]);

  // Filter to show only available therapists (and apply user filters)
  const availableTherapists = useMemo(() => {
    // Get current user's therapist name if they are a therapist
    const currentUserName = user?.name || user?.email;
    
    return therapists.filter(t => {
      // Must be rostered and available, OR be the current user (therapist viewing their own schedule)
      const isRostered = rosteredNames.has(t.name);
      const isCurrentUser = currentUserName && (t.name === currentUserName || t.email === user?.email);
      
      if (!isRostered && !isCurrentUser) return false;
      
      // For rostered therapists, check availability (current user always sees themselves)
      if (isRostered) {
        const shiftStatus = therapistShiftStatus.get(t.name);
        if (shiftStatus?.availability_type === 'UNAVAILABLE' && !isCurrentUser) return false;
      }
      
      // Apply role filter
      if (filters.role && normalizeRoleKey(getStaffRole(t)) !== normalizeRoleKey(filters.role)) return false;
      
      if (filters.searchText && !therapistMatchesSearchTokens(t, filters.searchText)) {
        return false;
      }
      
      // Apply guest name filter
      if (filters.guestName) {
        const hasGuestBooking = bookings.some(b => 
          bookingIncludesTherapist(b, t.name) && 
          (b.guestName === filters.guestName || b.guest_name === filters.guestName)
        );
        if (!hasGuestBooking) return false;
      }
      
      // Apply cabin filter (therapy treatment room)
      if (filters.cabin) {
        const hasCabinBooking = bookings.some(b => 
          bookingIncludesTherapist(b, t.name) && b.room === filters.cabin
        );
        if (!hasCabinBooking) return false;
      }
      
      // Apply guest room filter (guest accommodation room)
      if (filters.guestRoom) {
        const hasGuestRoomBooking = bookings.some(b => 
          bookingIncludesTherapist(b, t.name) && 
          (b.guestRoom === filters.guestRoom || b.guest_room === filters.guestRoom)
        );
        if (!hasGuestRoomBooking) return false;
      }
      
      // Apply block type filter
      if (filters.blockType) {
        const hasBlock = blocks.some(bl => 
          therapists.find(th => th.id === bl.therapist_id)?.name === t.name &&
          bl.block_type === filters.blockType
        );
        if (!hasBlock) return false;
      }
      
      return true;
    });
  }, [therapists, rosteredNames, therapistShiftStatus, filters, bookings, blocks, user]);

  const timeSlots = useMemo(() => {
    const slots = [];
    const start = 6 * 60; const end = 19 * 60;
    for (let t = start; t <= end; t += SLOT_MINUTES) {
      const h = Math.floor(t / 60), m = t % 60;
      slots.push({ t, label: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`});
    }
    return slots;
  }, [SLOT_MINUTES]);

  const colorStylesForStatus = (status) => ((status || '').toLowerCase() === 'blocked'
    ? { bg: '#fecaca', bd: '#f87171' }  // red-200 / red-400 - opaque
    : { bg: '#d1fae5', bd: '#6ee7b7' }); // green-200 / green-300 - opaque

  const colorStylesForBlockType = (blockType) => {
    const styles = {
      BLOCKED: { bg: '#fecaca', bd: '#f87171' },  // red - opaque
      BREAK: { bg: '#fed7aa', bd: '#fb923c' },    // orange - opaque
      MEETING: { bg: '#bfdbfe', bd: '#60a5fa' },  // blue - opaque
      TRAINING: { bg: '#e9d5ff', bd: '#c084fc' }, // purple - opaque
      ADMIN: { bg: '#cbd5e1', bd: '#64748b' },    // slate - opaque
      OTHER: { bg: '#e5e7eb', bd: '#9ca3af' }     // gray - opaque
    };
    return styles[blockType] || styles.BLOCKED;
  };

  const renderCell = (therapistName, slotMinutes) => {
    const therapist = therapists.find(t => t.name === therapistName);
    const therapistId = therapist?.id;
    
    // Get bookings for this therapist at this time slot
    const dayBookings = bookings.filter(b => bookingIncludesTherapist(b, therapistName));
    const atSlotBookings = dayBookings.filter(b => {
      if (!b.startTime) return false;
      const [sh, sm] = b.startTime.split(':').map(Number);
      const st = sh * 60 + sm;
      return Math.floor(st / SLOT_MINUTES) * SLOT_MINUTES === slotMinutes;
    });

    // Get blocks for this therapist at this time slot
    const therapistBlocks = blocks.filter(b => b.therapist_id === therapistId);
    const atSlotBlocks = therapistBlocks.filter(b => {
      if (!b.start_time) return false;
      const [sh, sm] = b.start_time.split(':').map(Number);
      const st = sh * 60 + sm;
      return Math.floor(st / SLOT_MINUTES) * SLOT_MINUTES === slotMinutes;
    });

    // Get shift breaks for this therapist at this time slot
    const shiftStatus = therapistShiftStatus.get(therapistName);
    const shiftBreaks = [];
    if (shiftStatus) {
      // Find the shift details for this therapist
      const dayAssignments = assignments[date] || {};
      for (const shiftData of Object.values(dayAssignments)) {
        if (shiftData.therapists && shiftData.therapists.includes(therapistName)) {
          const breaks = shiftData.shift_details?.breaks || [];
          breaks.forEach(br => {
            const [bsh, bsm] = br.start_time.split(':').map(Number);
            const bst = bsh * 60 + bsm;
            if (Math.floor(bst / SLOT_MINUTES) * SLOT_MINUTES === slotMinutes) {
              shiftBreaks.push(br);
            }
          });
        }
      }
    }

    const allItems = [
      ...atSlotBookings.map(b => ({ type: 'booking', data: b })),
      ...atSlotBlocks.map(b => ({ type: 'block', data: b })),
      ...shiftBreaks.map(b => ({ type: 'shift_break', data: b }))
    ];

    if (allItems.length === 0) return null;

    // Calculate width for each item if there are multiple
    const itemCount = allItems.length;
    const itemWidth = itemCount > 1 ? `${100 / itemCount}%` : '100%';

    return allItems.map((item, idx) => {
      if (item.type === 'booking') {
        const b = item.data;
        const guestName = getBookingGuestName(b);
        const treatmentInstruction = getBookingTreatmentInstruction(b);
        const generalInstruction = getBookingGeneralInstruction(b);
        const bookingTitle = formatBookingBlockTitle(b);
      const [sh, sm] = (b.startTime || b.start_time || '00:00').split(':').map(Number);
      const [eh, em] = (b.endTime || b.end_time || '00:00').split(':').map(Number);
      const st = (sh || 0) * 60 + (sm || 0);
      const et = (eh || 0) * 60 + (em || 0);
      const duration = Math.max(SLOT_MINUTES, (et > st ? (et - st) : SLOT_MINUTES));
      // Use exact pixel height based on actual duration, not rounded to slots
      const height = (duration / SLOT_MINUTES) * SLOT_HEIGHT;
      const scheduleConflict = getBookingScheduleConflict(b, therapistName);
      const colors = scheduleConflict
        ? { bg: '#fecaca', bd: '#dc2626' }
        : colorStylesForStatus(b.status);
      const leftPosition = itemCount > 1 ? `${(idx / itemCount) * 100}%` : '0';
      const bookingTitleWithConflict = scheduleConflict
        ? `${bookingTitle}\n⚠ ${scheduleConflict}`
        : bookingTitle;
      return (
          <div key={`booking-${b.bookingId || b.id || idx}`}
           className="absolute px-1 pt-0.5 text-[11px] overflow-hidden"
               style={{ 
                 height: `${height}px`, 
                 top: 0,
                 backgroundColor: colors.bg, 
                 border: scheduleConflict ? `2px solid ${colors.bd}` : `1px solid ${colors.bd}`, 
                 lineHeight: 1.15, 
                 zIndex: 10,
                 left: leftPosition,
                 width: itemWidth,
                 boxShadow: scheduleConflict ? 'inset 0 0 0 1px rgba(220, 38, 38, 0.25)' : undefined,
               }}>
        {isAdmin ? (
        <button
          className="w-full text-left"
          onClick={() => { setSelectedBooking(b); setShowModal(true); }}
          title={bookingTitleWithConflict}
        >
          {scheduleConflict && (
            <div className="truncate text-[10px] font-semibold text-red-800">⚠ During break</div>
          )}
          {viewMode === 'compact' ? (
            <>
              <div className="truncate">{b.treatment}</div>
              {treatmentInstruction && (
                <div className="truncate text-[10px] leading-tight opacity-90">{treatmentInstruction}</div>
              )}
              {generalInstruction && (
                <div className="truncate text-[10px] leading-tight opacity-90">General: {generalInstruction}</div>
              )}
              {b.is_upgrade && Number(b.upgrade_amount) > 0 && (
                <div className="truncate text-[10px] leading-tight font-semibold">
                  Upgrade ₹{Number(b.upgrade_amount).toLocaleString('en-IN')}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="truncate">
                {guestName ? `${b.treatment} | ${guestName}` : b.treatment}
              </div>
              {treatmentInstruction && (
                <div className="truncate text-[10px] leading-tight opacity-90">{treatmentInstruction}</div>
              )}
              {generalInstruction && (
                <div className="truncate text-[10px] leading-tight opacity-90">General: {generalInstruction}</div>
              )}
              {b.is_upgrade && Number(b.upgrade_amount) > 0 && (
                <div className="truncate text-[10px] leading-tight font-semibold">
                  Upgrade ₹{Number(b.upgrade_amount).toLocaleString('en-IN')}
                </div>
              )}
            </>
          )}
        </button>
        ) : (
        <div className="w-full text-left" title={bookingTitleWithConflict}>
          {scheduleConflict && (
            <div className="truncate text-[10px] font-semibold text-red-800">⚠ During break</div>
          )}
          {viewMode === 'compact' ? (
            <>
              <div className="truncate">{b.treatment}</div>
              {treatmentInstruction && (
                <div className="truncate text-[10px] leading-tight opacity-90">{treatmentInstruction}</div>
              )}
              {generalInstruction && (
                <div className="truncate text-[10px] leading-tight opacity-90">General: {generalInstruction}</div>
              )}
              {b.is_upgrade && Number(b.upgrade_amount) > 0 && (
                <div className="truncate text-[10px] leading-tight font-semibold">
                  Upgrade ₹{Number(b.upgrade_amount).toLocaleString('en-IN')}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="truncate">
                {guestName ? `${b.treatment} | ${guestName}` : b.treatment}
              </div>
              {treatmentInstruction && (
                <div className="truncate text-[10px] leading-tight opacity-90">{treatmentInstruction}</div>
              )}
              {generalInstruction && (
                <div className="truncate text-[10px] leading-tight opacity-90">General: {generalInstruction}</div>
              )}
              {b.is_upgrade && Number(b.upgrade_amount) > 0 && (
                <div className="truncate text-[10px] leading-tight font-semibold">
                  Upgrade ₹{Number(b.upgrade_amount).toLocaleString('en-IN')}
                </div>
              )}
            </>
          )}
        </div>
        )}
      </div>
        );
      } else if (item.type === 'shift_break') {
        // Render shift break
        const shiftBreak = item.data;
        const [sh, sm] = shiftBreak.start_time.split(':').map(Number);
        const [eh, em] = shiftBreak.end_time.split(':').map(Number);
        const st = sh * 60 + sm;
        const et = eh * 60 + em;
        const duration = Math.max(SLOT_MINUTES, (et > st ? (et - st) : SLOT_MINUTES));
        // Use exact pixel height based on actual duration, not rounded to slots
        const height = (duration / SLOT_MINUTES) * SLOT_HEIGHT;
        // Use BREAK styling
        const colors = { bg: '#fed7aa', bd: '#fb923c' };  // orange - opaque
        const leftPosition = itemCount > 1 ? `${(idx / itemCount) * 100}%` : '0';
        return (
          <div key={`shift-break-${idx}`}
               className="absolute px-1 pt-0.5 text-[11px] overflow-hidden"
               style={{ 
                 height: `${height}px`, 
                 top: 0,
                 backgroundColor: colors.bg, 
                 border: `1px solid ${colors.bd}`, 
                 lineHeight: 1.15, 
                 zIndex: 4,
                 left: leftPosition,
                 width: itemWidth
               }}
               title={`Shift Break: ${shiftBreak.description || shiftBreak.break_type}`}
          >
            <div className="font-semibold">☕ {shiftBreak.break_type || 'BREAK'}</div>
            {shiftBreak.description && (
              <div className="text-[10px] text-gray-700 mt-0.5 break-words">
                {shiftBreak.description}
              </div>
            )}
          </div>
        );
      } else {
        // Render block
        const block = item.data;
        const [sh, sm] = block.start_time.split(':').map(Number);
        const [eh, em] = block.end_time.split(':').map(Number);
        const st = sh * 60 + sm;
        const et = eh * 60 + em;
        const duration = Math.max(SLOT_MINUTES, (et > st ? (et - st) : SLOT_MINUTES));
        // Use exact pixel height based on actual duration, not rounded to slots
        const height = (duration / SLOT_MINUTES) * SLOT_HEIGHT;
        const colors = colorStylesForBlockType(block.block_type);
        const leftPosition = itemCount > 1 ? `${(idx / itemCount) * 100}%` : '0';
        return (
          <div key={`block-${block.id}`}
               className="absolute px-1 pt-0.5 text-[11px] overflow-hidden"
               style={{ 
                 height: `${height}px`, 
                 top: 0,
                 backgroundColor: colors.bg, 
                 border: `1px solid ${colors.bd}`, 
                 lineHeight: 1.15, 
                 zIndex: 5,
                 left: leftPosition,
                 width: itemWidth
               }}>
            {isAdmin ? (
            <button
              className="w-full text-left h-full"
              onClick={() => { setSelectedBlock(block); setShowBlockModal(true); }}
              title={`${block.block_type}: ${block.reason || 'No reason'}`}
            >
              <div className="font-semibold">🚫 {block.block_type}</div>
              {block.reason && (
                <div className="text-[10px] text-gray-700 mt-0.5 break-words">
                  {block.reason}
                </div>
              )}
            </button>
            ) : (
            <div className="w-full text-left h-full">
              <div className="font-semibold">🚫 {block.block_type}</div>
              {block.reason && (
                <div className="text-[10px] text-gray-700 mt-0.5 break-words">
                  {block.reason}
                </div>
              )}
            </div>
            )}
          </div>
        );
      }
    });
  };

  const therapistUnavailableReason = (therapistName, booking) => {
    if (!therapistName || !booking) return null;
    return getBookingScheduleConflict(booking, therapistName);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, gap: 2, bgcolor: '#f6f6f6' }}>
      <Paper sx={{ p: 2, bgcolor: '#fff', border: '1px solid #e5e7eb', boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Duty Roster</Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Availability Summary */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Box 
                sx={{ display: 'flex', gap: 0.5, alignItems: 'center', px: 1.5, py: 0.5, bgcolor: '#f0fdf4', border: '1px solid #86efac', borderRadius: 1 }}
                title="Rostered with AVAILABLE shifts (operational)"
              >
                <span style={{ fontSize: '14px' }}>✓</span>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#16a34a', fontSize: '0.875rem' }}>
                  {availabilitySummary.available}
                </Typography>
              </Box>
              <Box 
                sx={{ display: 'flex', gap: 0.5, alignItems: 'center', px: 1.5, py: 0.5, bgcolor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 1 }}
                title="Rostered with UNAVAILABLE shifts (leaves, off)"
              >
                <span style={{ fontSize: '14px' }}>✗</span>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626', fontSize: '0.875rem' }}>
                  {availabilitySummary.unavailable}
                </Typography>
              </Box>
              <Box 
                sx={{ display: 'flex', gap: 0.5, alignItems: 'center', px: 1.5, py: 0.5, bgcolor: '#f9fafb', border: '1px solid #d1d5db', borderRadius: 1 }}
                title="Not rostered (no shift assigned)"
              >
                <span style={{ fontSize: '14px' }}>—</span>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#6b7280', fontSize: '0.875rem' }}>
                  {availabilitySummary.offDuty}
                </Typography>
              </Box>
              {bookingBreakConflicts > 0 && (
                <Box
                  sx={{ display: 'flex', gap: 0.5, alignItems: 'center', px: 1.5, py: 0.5, bgcolor: '#fef2f2', border: '1px solid #f87171', borderRadius: 1 }}
                  title="Bookings overlapping a shift break or blocked time"
                >
                  <span style={{ fontSize: '14px' }}>⚠</span>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#b91c1c', fontSize: '0.875rem' }}>
                    {bookingBreakConflicts} break conflict{bookingBreakConflicts !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Date and View Mode Controls */}
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border rounded px-2 py-1" />
            <ButtonGroup size="small" variant="outlined">
              <Button onClick={() => setViewMode('normal')} variant={viewMode==='normal'?'contained':'outlined'}>Normal</Button>
              <Button onClick={() => setViewMode('compact')} variant={viewMode==='compact'?'contained':'outlined'}>Compact</Button>
            </ButtonGroup>
            
            {/* Filter Toggle Button */}
            <Button
              size="small"
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ 
                bgcolor: showFilters ? '#E4C984' : 'transparent',
                color: showFilters ? '#171717' : 'inherit',
                '&:hover': { bgcolor: showFilters ? '#d4b974' : 'rgba(0,0,0,0.04)' }
              }}
            >
              Filters {Object.values(filters).filter(v => v).length > 0 && `(${Object.values(filters).filter(v => v).length})`}
            </Button>
          </Box>
        </Box>
        
        {/* Filters Section */}
        <Collapse in={showFilters}>
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e5e7eb' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1.5 }}>
              <TextField
                size="small"
                label="Search Therapist"
                value={filters.searchText}
                onChange={(e) => setFilters({ ...filters, searchText: e.target.value })}
                placeholder="Type name..."
                sx={{ 
                  '& .MuiInputBase-root': { fontSize: '0.875rem', height: '36px' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' }
                }}
                InputProps={{
                  endAdornment: filters.searchText && (
                    <IconButton size="small" onClick={() => setFilters({ ...filters, searchText: '' })}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )
                }}
              />
              
              <FormControl 
                size="small"
                sx={{ 
                  '& .MuiInputBase-root': { fontSize: '0.875rem', height: '36px' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                  '& .MuiMenuItem-root': { fontSize: '0.875rem' }
                }}
              >
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role}
                  label="Role"
                  onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {filterOptions.roles.map(role => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl 
                size="small"
                sx={{ 
                  '& .MuiInputBase-root': { fontSize: '0.875rem', height: '36px' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                  '& .MuiMenuItem-root': { fontSize: '0.875rem' }
                }}
              >
                <InputLabel>Guest</InputLabel>
                <Select
                  value={filters.guestName}
                  label="Guest"
                  onChange={(e) => setFilters({ ...filters, guestName: e.target.value })}
                >
                  <MenuItem value="">All Guests</MenuItem>
                  {filterOptions.guests.map(guest => (
                    <MenuItem key={guest} value={guest}>{guest}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl 
                size="small"
                sx={{ 
                  '& .MuiInputBase-root': { fontSize: '0.875rem', height: '36px' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                  '& .MuiMenuItem-root': { fontSize: '0.875rem' }
                }}
              >
                <InputLabel>Cabin</InputLabel>
                <Select
                  value={filters.cabin}
                  label="Cabin"
                  onChange={(e) => setFilters({ ...filters, cabin: e.target.value })}
                >
                  <MenuItem value="">All Cabins</MenuItem>
                  {filterOptions.cabins.map(cabin => (
                    <MenuItem key={cabin} value={cabin}>Cabin {cabin}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl 
                size="small"
                sx={{ 
                  '& .MuiInputBase-root': { fontSize: '0.875rem', height: '36px' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                  '& .MuiMenuItem-root': { fontSize: '0.875rem' }
                }}
              >
                <InputLabel>Guest Room</InputLabel>
                <Select
                  value={filters.guestRoom}
                  label="Guest Room"
                  onChange={(e) => setFilters({ ...filters, guestRoom: e.target.value })}
                >
                  <MenuItem value="">All Guest Rooms</MenuItem>
                  {filterOptions.guestRooms.map(room => (
                    <MenuItem key={room} value={room}>Room {room}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl 
                size="small"
                sx={{ 
                  '& .MuiInputBase-root': { fontSize: '0.875rem', height: '36px' },
                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                  '& .MuiMenuItem-root': { fontSize: '0.875rem' }
                }}
              >
                <InputLabel>Block Type</InputLabel>
                <Select
                  value={filters.blockType}
                  label="Block Type"
                  onChange={(e) => setFilters({ ...filters, blockType: e.target.value })}
                >
                  <MenuItem value="">All Block Types</MenuItem>
                  {filterOptions.blockTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                Showing {availableTherapists.length} therapist{availableTherapists.length !== 1 ? 's' : ''}
              </Typography>
              {Object.values(filters).some(v => v) && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ClearIcon />}
                  onClick={() => setFilters({ role: '', guestName: '', cabin: '', guestRoom: '', blockType: '', searchText: '' })}
                  sx={{ ml: 'auto', fontSize: '0.8rem', textTransform: 'none' }}
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          </Box>
        </Collapse>
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          {legend.map(l => (
            <Chip key={l.label} label={l.label} size="small" sx={{ bgcolor: l.bg, color: '#171717', border: `1px solid ${l.bd}` }} />
          ))}
        </Box>
      </Paper>

      <Paper sx={{ flex: 1, p: 0, overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        <table className="w-full border-collapse relative">
          <thead className="sticky top-0 bg-white z-20">
            <tr>
              <th className="sticky left-0 bg-gray-50 z-[15] p-1 border">Time</th>
              {availableTherapists.map(t => {
                const shiftStatus = therapistShiftStatus.get(t.name);
                
                return (
                <th key={t.name} className="p-1 border bg-gray-50 min-w-[220px]">
                  <div className="font-bold">{t.name}</div>
                  {isAdmin ? (
                    <button
                      className="flex items-center justify-center gap-1 text-xs mt-1 w-full hover:bg-green-50 rounded px-2 py-1 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedTherapistForShift({ id: t.id, name: t.name, currentShift: shiftStatus?.shift_code });
                        setNewShiftCode(shiftStatus?.shift_code || '');
                        setShowShiftChangeModal(true);
                      }}
                      title="Click to change shift"
                    >
                      <span style={{ color: '#16a34a' }}>✓</span>
                      <span style={{ color: '#16a34a', fontWeight: 500 }}>
                        {shiftStatus.shift_code} - Available
                      </span>
                    </button>
                  ) : (
                    <div className="flex items-center justify-center gap-1 text-xs mt-1 w-full px-2 py-1">
                      <span style={{ color: '#16a34a' }}>✓</span>
                      <span style={{ color: '#16a34a', fontWeight: 500 }}>
                        {shiftStatus.shift_code} - Available
                      </span>
                    </div>
                  )}
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map(slot => (
              <tr key={slot.label} style={{ height: `${SLOT_HEIGHT}px` }}>
                <td className="sticky left-0 bg-white z-[15] p-1 border text-sm">{slot.label}</td>
                {availableTherapists.map(t => (
                  <td 
                    key={`${slot.label}-${t.name}`} 
                    className="p-1 border relative cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={(e) => {
                      // Only trigger if clicking on empty space (not on existing booking/block) and user is admin
                      if (isAdmin && e.target.tagName === 'TD') {
                        const therapist = availableTherapists.find(th => th.name === t.name);
                        const slotHour = Math.floor(slot.t / 60);
                        const slotMin = slot.t % 60;
                        const startTime = `${String(slotHour).padStart(2,'0')}:${String(slotMin).padStart(2,'0')}`;
                        const endHour = Math.floor((slot.t + 60) / 60);
                        const endMin = (slot.t + 60) % 60;
                        const endTime = `${String(endHour).padStart(2,'0')}:${String(endMin).padStart(2,'0')}`;
                        
                        setBlockForm({
                          therapist_id: therapist?.id || t.id,
                          therapist_name: t.name,
                          start_time: startTime,
                          end_time: endTime,
                          block_type: 'BLOCKED',
                          reason: '',
                          duration: 60
                        });
                        setSelectedBlock(null);
                        setShowBlockModal(true);
                      }
                    }}
                  >
                    {renderCell(t.name, slot.t)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Paper>

      <Dialog open={showModal && !!selectedBooking} onClose={() => { setShowModal(false); setSelectedBooking(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Duty</DialogTitle>
        <DialogContent dividers>
          {selectedBooking && (
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {Array.isArray(selectedBooking.therapists) && selectedBooking.therapists.length > 1
                  ? selectedBooking.therapists
                      .map((t) => `${t.name}${t.is_primary ? ' (Primary)' : ''}`)
                      .join(', ')
                  : selectedBooking.therapist}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {selectedBooking.treatment} • Room {selectedBooking.room} • {selectedBooking.startTime}–{selectedBooking.endTime}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>Guest: {selectedBooking.guestName}</Typography>
              {getBookingTreatmentInstruction(selectedBooking) && (
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Treatment instructions: {getBookingTreatmentInstruction(selectedBooking)}
                </Typography>
              )}
              {getBookingGeneralInstruction(selectedBooking) && (
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  General instructions: {getBookingGeneralInstruction(selectedBooking)}
                </Typography>
              )}
              {selectedBooking.is_upgrade && Number(selectedBooking.upgrade_amount) > 0 && (
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
                  Upgrade charge: ₹{Number(selectedBooking.upgrade_amount).toLocaleString('en-IN')}
                  {selectedBooking.upgrade_status ? ` (${selectedBooking.upgrade_status})` : ''}
                </Typography>
              )}
              {selectedBooking && therapistUnavailableReason(
                getBookingTherapistNames(selectedBooking)[0] || selectedBooking.therapist,
                selectedBooking,
              ) && (
                <Typography variant="body2" sx={{ mb: 2, color: '#b91c1c', fontWeight: 600 }}>
                  {therapistUnavailableReason(
                    getBookingTherapistNames(selectedBooking)[0] || selectedBooking.therapist,
                    selectedBooking,
                  )}
                </Typography>
              )}
              <FormControl fullWidth size="small">
                <InputLabel>Change Therapist</InputLabel>
                <Select value={reassignTherapist} label="Change Therapist" onChange={(e) => {
                  const nextName = e.target.value;
                  const unavailable = therapistUnavailableReason(nextName, selectedBooking);
                  if (unavailable) {
                    window.alert(`${nextName} cannot take this appointment. ${unavailable}`);
                    return;
                  }
                  setReassignTherapist(nextName);
                }}>
                  {availableTherapists
                    .filter(t => {
                      // Filter out therapists with overlapping bookings at this time
                      const [sh, sm] = selectedBooking.startTime.split(':').map(Number);
                      const [eh, em] = selectedBooking.endTime.split(':').map(Number);
                      const st = sh*60+sm, et = eh*60+em;
                      return !bookings.some(b => bookingIncludesTherapist(b, t.name) && (()=>{
                        const [bsh,bsm] = b.startTime.split(':').map(Number);
                        const [beh,bem] = b.endTime.split(':').map(Number);
                        const bst = bsh*60+bsm, bet = beh*60+bem;
                        return Math.max(st, bst) < Math.min(et, bet);
                      })());
                    })
                    .map(t => (
                      <MenuItem key={t.name} value={t.name}>{t.name}</MenuItem>
                    ))}
                </Select>
              </FormControl>
              {availableTherapists.filter(t => {
                const [sh, sm] = selectedBooking.startTime.split(':').map(Number);
                const [eh, em] = selectedBooking.endTime.split(':').map(Number);
                const st = sh*60+sm, et = eh*60+em;
                return !bookings.some(b => bookingIncludesTherapist(b, t.name) && (()=>{
                  const [bsh,bsm] = b.startTime.split(':').map(Number);
                  const [beh,bem] = b.endTime.split(':').map(Number);
                  const bst = bsh*60+bsm, bet = beh*60+bem;
                  return Math.max(st, bst) < Math.min(et, bet);
                })());
              }).length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  No available therapists for this time slot
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowModal(false); setSelectedBooking(null); setReassignTherapist(''); }}>Close</Button>
          <Button
            onClick={async () => {
              if (!selectedBooking || !reassignTherapist) return;
              const unavailable = therapistUnavailableReason(reassignTherapist, selectedBooking);
              if (unavailable) {
                window.alert(`${reassignTherapist} cannot take this appointment. ${unavailable} This change was not saved.`);
                return;
              }
              const response = await apiPatch(`/api/bookings/${selectedBooking.bookingId || selectedBooking.id}/assign`, { date, therapist: reassignTherapist });
              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                window.alert(errorData.detail || 'Could not change the therapist.');
                return;
              }
              const bks = await apiGet(`/api/bookings/${date}`).then(r => r.json());
              setBookings(Array.isArray(bks) ? bks : []);
              setShowModal(false); setSelectedBooking(null); setReassignTherapist('');
            }}
            disabled={!reassignTherapist}
            variant="contained" sx={{ bgcolor: '#E4C984', color: '#171717' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block Management Dialog */}
      <Dialog open={showBlockModal} onClose={() => { setShowBlockModal(false); setSelectedBlock(null); }} fullWidth maxWidth="sm">
        <DialogTitle>{selectedBlock ? 'Block Details' : 'Create Time Block'}</DialogTitle>
        <DialogContent dividers>
          {selectedBlock ? (
            // View/Edit existing block
            <>
              <Typography variant="h6" sx={{ mb: 1 }}>{blocks.find(b => b.id === selectedBlock.id)?.therapist_name || 'Unknown'}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {selectedBlock.block_type} • {selectedBlock.start_time} – {selectedBlock.end_time}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>Reason: {selectedBlock.reason || 'No reason provided'}</Typography>
              <Typography variant="caption" color="text.secondary">
                Created by: {selectedBlock.created_by || 'Unknown'} • {new Date(selectedBlock.created_at).toLocaleString()}
              </Typography>
            </>
          ) : (
            // Create new block
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Block time for: <strong>{blockForm.therapist_name}</strong>
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <input 
                    type="time" 
                    value={blockForm.start_time} 
                    onChange={(e) => setBlockForm({...blockForm, start_time: e.target.value})}
                    className="border rounded px-2 py-1 flex-1"
                  />
                  <input 
                    type="time" 
                    value={blockForm.end_time} 
                    onChange={(e) => setBlockForm({...blockForm, end_time: e.target.value})}
                    className="border rounded px-2 py-1 flex-1"
                  />
                </Box>
                <FormControl fullWidth size="small">
                  <InputLabel>Block Type</InputLabel>
                  <Select 
                    value={blockForm.block_type} 
                    label="Block Type"
                    onChange={(e) => setBlockForm({...blockForm, block_type: e.target.value})}
                  >
                    <MenuItem value="BLOCKED">🚫 Blocked</MenuItem>
                    <MenuItem value="BREAK">☕ Break</MenuItem>
                    <MenuItem value="MEETING">👥 Meeting</MenuItem>
                    <MenuItem value="TRAINING">📚 Training</MenuItem>
                    <MenuItem value="ADMIN">📋 Admin</MenuItem>
                    <MenuItem value="OTHER">➕ Other</MenuItem>
                  </Select>
                </FormControl>
                <input 
                  type="text" 
                  placeholder="Reason (optional)" 
                  value={blockForm.reason}
                  onChange={(e) => setBlockForm({...blockForm, reason: e.target.value})}
                  className="border rounded px-2 py-2 w-full"
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {selectedBlock ? (
            <>
              <Button 
                onClick={async () => {
                  if (!selectedBlock) return;
                  await apiDelete(`/api/blocks/${selectedBlock.id}`);
                  const blks = await apiGet(`/api/blocks?date=${date}`).then(r => r.json()).catch(() => []);
                  setBlocks(Array.isArray(blks) ? blks : []);
                  setShowBlockModal(false);
                  setSelectedBlock(null);
                }}
                color="error"
              >
                Delete Block
              </Button>
              <Button onClick={() => { setShowBlockModal(false); setSelectedBlock(null); }}>Close</Button>
            </>
          ) : (
            <>
              <Button onClick={() => { setShowBlockModal(false); setSelectedBlock(null); }}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!blockForm.therapist_id) {
                    alert('Please select a therapist cell to create a block');
                    return;
                  }
                  if (!blockForm.start_time || !blockForm.end_time) {
                    alert('Please select start and end times');
                    return;
                  }
                  
                  try {
                    const payload = {
                      therapist_id: blockForm.therapist_id,
                      date: date,
                      start_time: blockForm.start_time,
                      end_time: blockForm.end_time,
                      block_type: blockForm.block_type,
                      reason: blockForm.reason || '',
                      created_by: 'admin'
                    };
                    
                    const response = await apiPost('/api/blocks', payload);
                    
                    if (response.ok) {
                      const blks = await apiGet(`/api/blocks?date=${date}`).then(r => r.json()).catch(() => []);
                      setBlocks(Array.isArray(blks) ? blks : []);
                      setShowBlockModal(false);
                      setBlockForm({
                        therapist_id: null,
                        start_time: '',
                        end_time: '',
                        block_type: 'BLOCKED',
                        reason: '',
                        duration: 60
                      });
                    } else {
                      const error = await response.json();
                      alert(error.detail || 'Failed to create block');
                    }
                  } catch (error) {
                    alert('Failed to create block: ' + error.message);
                  }
                }}
                variant="contained"
                sx={{ bgcolor: '#E4C984', color: '#171717', '&:hover': { bgcolor: '#d4b974' } }}
              >
                Create Block
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Shift Change Modal */}
      <Dialog 
        open={showShiftChangeModal} 
        onClose={() => { 
          setShowShiftChangeModal(false); 
          setSelectedTherapistForShift(null); 
          setNewShiftCode(''); 
        }} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle>Change Shift Assignment</DialogTitle>
        <DialogContent dividers>
          {selectedTherapistForShift && (
            <>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                {selectedTherapistForShift.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Current Shift: <strong>{selectedTherapistForShift.currentShift || 'None'}</strong>
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Select New Shift</InputLabel>
                <Select 
                  value={newShiftCode} 
                  label="Select New Shift" 
                  onChange={(e) => setNewShiftCode(e.target.value)}
                >
                  <MenuItem value="">-- Unassign (Off Duty) --</MenuItem>
                  {availableShifts.map(shift => (
                    <MenuItem key={shift.shift_code} value={shift.shift_code}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '2px', 
                            bgcolor: shift.color || '#f0f0f0',
                            border: '1px solid #ddd'
                          }} 
                        />
                        <span>{shift.shift_code} - {shift.name}</span>
                        <Chip 
                          label={shift.availability_type || 'AVAILABLE'} 
                          size="small"
                          sx={{ 
                            ml: 1,
                            fontSize: '0.65rem',
                            height: '18px',
                            bgcolor: shift.availability_type === 'UNAVAILABLE' ? '#fef2f2' : '#f0fdf4',
                            color: shift.availability_type === 'UNAVAILABLE' ? '#dc2626' : '#16a34a',
                            border: shift.availability_type === 'UNAVAILABLE' ? '1px solid #fca5a5' : '1px solid #86efac'
                          }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { 
            setShowShiftChangeModal(false); 
            setSelectedTherapistForShift(null); 
            setNewShiftCode(''); 
          }}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!selectedTherapistForShift) return;
              
              try {
                // If newShiftCode is empty, unassign the therapist
                if (!newShiftCode) {
                  const response = await apiDelete(`/api/shifts/unassign/${date}/${selectedTherapistForShift.name}`);
                  if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.detail || 'Failed to unassign therapist');
                  }
                } else {
                  // Assign to new shift
                  const response = await apiPost('/api/shifts/assign', { 
                      shift_code: newShiftCode,
                      date: date, 
                      therapist_ids: [selectedTherapistForShift.name] 
                    });
                  if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.detail || 'Failed to assign shift');
                  }
                }
                
                // Refresh assignments
                const assn = await apiGet(`/api/roster/assignments?start_date=${date}&end_date=${date}`).then(r => r.json());
                setAssignments(assn || {});
                
                setShowShiftChangeModal(false);
                setSelectedTherapistForShift(null);
                setNewShiftCode('');
              } catch (error) {
                alert('Failed to change shift: ' + error.message);
              }
            }}
            variant="contained"
            sx={{ bgcolor: '#E4C984', color: '#171717', '&:hover': { bgcolor: '#d4b974' } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DutyRoster;
